import { BUILDINGS } from './buildings.js';

/**
 * Centralized task definitions - single source of truth for all task metadata
 *
 * @typedef {Object} TaskDefinition
 * @property {string} id - Unique task identifier
 * @property {string} label - Display label with emoji
 * @property {string} emoji - Just the emoji
 * @property {string} description - Short description
 * @property {string|null} resourceOutput - Resource produced (null if none)
 * @property {number} outputDivisor - Divide total by this for resource output (default 1)
 * @property {string} category - Task category ('gather', 'defense', 'knowledge', 'special')
 * @property {string|null} requiredBuilding - Building required to unlock (null if always available)
 * @property {boolean} isOutdoorTask - Whether storm events affect this task
 * @property {boolean} canPush - Whether players can push for extra dice
 * @property {string|null} soundEffect - Audio effect to play (null if none)
 * @property {string} [specialMechanic] - Special behavior flag (optional)
 * @property {Function} [showCondition] - Custom visibility logic (optional)
 *
 * All task information should be retrieved from TASK_DEFINITIONS.
 * Use helper functions: getTaskSoundEffect(), isOutdoorTask(), getAvailableTasks()
 */
export const TASK_DEFINITIONS = {
  farm: {
    id: 'farm',
    label: '🌾 Farm',
    emoji: '🌾',
    description: 'Gather food',
    resourceOutput: 'food',
    outputDivisor: 1,
    category: 'gather',
    requiredBuilding: null,
    isOutdoorTask: true,
    canPush: true,
    soundEffect: 'task_gather'
  },

  chop: {
    id: 'chop',
    label: '🪓 Chop Wood',
    emoji: '🪓',
    description: 'Gather wood',
    resourceOutput: 'wood',
    outputDivisor: 1,
    category: 'gather',
    requiredBuilding: null,
    isOutdoorTask: true,
    canPush: true,
    soundEffect: 'task_chop'
  },

  quarry: {
    id: 'quarry',
    label: '⛏️ Quarry Stone',
    emoji: '⛏️',
    description: 'Gather stone',
    resourceOutput: 'stone',
    outputDivisor: 1,
    category: 'gather',
    requiredBuilding: null,
    isOutdoorTask: true,
    canPush: true,
    soundEffect: 'task_gather'
  },

  guard: {
    id: 'guard',
    label: '🛡️ Guard',
    emoji: '🛡️',
    description: 'Defend against raids',
    resourceOutput: null,
    category: 'defense',
    requiredBuilding: null,
    isOutdoorTask: false,
    canPush: true,
    soundEffect: null
  },

  build: {
    id: 'build',
    label: '🔨 Build',
    emoji: '🔨',
    description: 'Construct buildings',
    resourceOutput: 'buildProgress',
    outputDivisor: 1,
    category: 'special',
    requiredBuilding: null,
    isOutdoorTask: false,
    canPush: true,
    soundEffect: 'task_build',
    specialMechanic: 'requires_selection',
    effectHandlers: {
      onAssign: 'showBuildingDialog'
    },
    // Only show build task when there's something to build
    showCondition: (gameState) => {
      const hasInProgress = gameState.buildingInProgress !== null;
      const hasUnbuilt = Object.keys(BUILDINGS).some(
        type => !gameState.buildings.some(b => b.type === type)
      );
      return hasInProgress || hasUnbuilt;
    }
  },

  rest: {
    id: 'rest',
    label: '💤 Rest',
    emoji: '💤',
    description: 'Recover from injuries',
    resourceOutput: null,
    category: 'special',
    requiredBuilding: null,
    isOutdoorTask: false,
    canPush: false,
    soundEffect: null,
    specialMechanic: 'healing',
    effectHandlers: {
      onEndDay: 'healRestingWorkers'
    }
  },

  research: {
    id: 'research',
    label: '🔬 Research',
    emoji: '🔬',
    description: 'Generate tech points',
    resourceOutput: 'techPoints',
    outputDivisor: 2,
    category: 'knowledge',
    requiredBuilding: 'workshop',
    isOutdoorTask: false,
    canPush: true,
    soundEffect: 'task_knowledge'
  },

  mine: {
    id: 'mine',
    label: '⛏️ Mine',
    emoji: '⛏️',
    description: 'Extract metal (cave-in risk on natural 1!)',
    resourceOutput: 'metal',
    outputDivisor: 1,
    category: 'gather',
    requiredBuilding: 'mine',
    isOutdoorTask: false,
    canPush: true,
    soundEffect: 'task_gather',
    specialMechanic: 'cave_in',
    caveInThreshold: 1, // Only natural 1 causes cave-in
    effectHandlers: {
      onRoll: 'handleCaveIn',
      onEndDay: 'handleMineInjuries'
    }
  },

  trade: {
    id: 'trade',
    label: '💰 Trade',
    emoji: '💰',
    description: 'Exchange resources',
    resourceOutput: null,
    category: 'special',
    requiredBuilding: 'marketplace',
    isOutdoorTask: false,
    canPush: false,
    soundEffect: 'ui_confirm',
    specialMechanic: 'trade_dialog'
  },

  treat: {
    id: 'treat',
    label: '💊 Treat Sick',
    emoji: '💊',
    description: 'Heal sick workers',
    resourceOutput: null,
    category: 'special',
    requiredBuilding: 'hospital',
    isOutdoorTask: false,
    canPush: false,
    soundEffect: 'ui_positive',
    specialMechanic: 'healing',
    effectHandlers: {
      onEndDay: 'healSickWorkers'
    }
  },

  study: {
    id: 'study',
    label: '📚 Study',
    emoji: '📚',
    description: 'Generate knowledge',
    resourceOutput: 'knowledge',
    outputDivisor: 2,
    category: 'knowledge',
    requiredBuilding: 'library',
    isOutdoorTask: false,
    canPush: true,
    soundEffect: 'task_knowledge'
  }
};

/**
 * Get all tasks available for the current game state
 * @param {Object} gameState - Current game state with buildings
 * @returns {Array} Array of task definition objects
 */
export function getAvailableTasks(gameState) {
  return Object.values(TASK_DEFINITIONS).filter(task => {
    // Check building requirement
    if (task.requiredBuilding) {
      const hasBuilding = gameState.buildings.some(
        b => b.type === task.requiredBuilding && b.complete
      );
      if (!hasBuilding) return false;
    }

    // Check custom show condition
    if (task.showCondition) {
      return task.showCondition(gameState);
    }

    return true;
  });
}

/**
 * Get task definition by ID
 * @param {string} taskId - Task identifier
 * @returns {Object|null} Task definition or null if not found
 */
export function getTask(taskId) {
  return TASK_DEFINITIONS[taskId] || null;
}

/**
 * Get sound effect for a task
 * @param {string} taskId - Task identifier
 * @returns {string|null} Sound effect ID or null
 */
export function getTaskSoundEffect(taskId) {
  return TASK_DEFINITIONS[taskId]?.soundEffect || null;
}

/**
 * Check if a task is an outdoor task
 * @param {string} taskId - Task identifier
 * @returns {boolean} True if outdoor task
 */
export function isOutdoorTask(taskId) {
  return TASK_DEFINITIONS[taskId]?.isOutdoorTask || false;
}

/**
 * Get all outdoor task IDs
 * @returns {Array<string>} Array of outdoor task IDs
 */
export function getOutdoorTasks() {
  return Object.values(TASK_DEFINITIONS)
    .filter(task => task.isOutdoorTask)
    .map(task => task.id);
}
